import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { URLLoader } from 'src/app/main/configs/URLLoader';
import MemberMessage from 'src/app/main/messages/MemberMessage';
import MemberTestService from 'src/app/main/mocks/MemberTestService';
import Member from 'src/app/main/models/Member';
import { HTTPService } from 'src/app/main/services/HTTPService';
import CONFIG from 'src/app/main/urls/urls';

@Component({
  selector: 'app-member',
  templateUrl: './member.component.html',
  styleUrls: ['./member.component.css'],
})
export class MemberComponent extends URLLoader implements OnInit {
  showsummary: boolean = false;
  showgraphic: boolean = false;
  members$;
  id = 0;
  memberI18n;
  loading = false;
  newArrivals: number;
  unverified: number;
  blacklist: number;
  expired: number;

  // ── Import state ────────────────────────────────────────────────────
  selectedFile: File | null = null;
  selectedFileName: string = '';
  importing = false;
  importResult: { imported: number; skipped: number; errors: string[] } | null = null;

  constructor(
    private toastr: ToastrService,
    private memberTestService: MemberTestService,
    private messageService: MemberMessage,
    private httpService: HTTPService,
    private http: HttpClient,
    private router: Router
  ) {
    super();
  }

  // ── Lifecycle ────────────────────────────────────────────────────────

  ngOnInit() {
    this.getAll();
    this.getMemberByLang(CONFIG.getInstance().getLang());
    this.loadBlockedMembers();
    this.loadExpiredAccounts();
    this.loadNewArrivals();
    this.loadUnverifiedAccounts();
  }

  // ── i18n ─────────────────────────────────────────────────────────────

  getMemberByLang(lang) {
    lang = 'EN';
    this.httpService.getAll(CONFIG.URL_BASE + '/i18n/member/' + lang).subscribe(
      (data) => { this.memberI18n = data; },
      (err: HttpErrorResponse) => { super.show('Error', err.message, 'error'); }
    );
  }

  // ── Navigation helpers ───────────────────────────────────────────────

  setId(id) { this.id = id; }
  edit(id)  { this.setId(id); }
  view(id)  { this.setId(id); }

  reloadPage() {
    this.router
      .navigateByUrl('/dashboard', { skipLocationChange: true })
      .then(() => this.router.navigate(['/member']));
  }

  // ── CRUD ─────────────────────────────────────────────────────────────

  delete(id: number): void {
    if (!confirm('Do you want to delete this recording?')) return;
    this.httpService.remove(CONFIG.URL_BASE + '/member/delete/' + id)
      .then(() => {
        this.toastr.success('Item removed successfully!', 'Success');
        this.getAll();
      })
      .catch(() => this.toastr.error('Error removing item', 'Error'));
  }

  getAll() {
    this.loading = true;
    this.httpService.getAll(CONFIG.URL_BASE + '/member/all').subscribe(
      (data: Member[]) => { this.members$ = data; this.loading = false; },
      (err: HttpErrorResponse) => { super.show('Error', err.message, 'error'); }
    );
  }

  // ── Summary card loaders ─────────────────────────────────────────────

  loadNewArrivals(): void {
    this.httpService.getNewArrivals().subscribe(
      data => this.newArrivals = data.length,
      error => console.error('Error fetching new arrivals:', error)
    );
  }

  loadUnverifiedAccounts(): void {
    this.httpService.getUnverifiedAccounts().subscribe(
      data => this.unverified = data.length,
      error => console.error('Error fetching unverified accounts:', error)
    );
  }

  loadBlockedMembers(): void {
    this.httpService.getBlockedMembers().subscribe(
      data => this.blacklist = data.length,
      error => console.error('Error fetching blocked members:', error)
    );
  }

  loadExpiredAccounts(): void {
    this.httpService.getExpiredAccounts().subscribe(
      data => this.expired = data.length,
      error => console.error('Error fetching expired accounts:', error)
    );
  }

  // ── Import ───────────────────────────────────────────────────────────

  /** Capture file from the <input type="file"> */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile     = input.files[0];
      this.selectedFileName = this.selectedFile.name;
      this.importResult     = null; // clear any previous result
    }
  }

  /** POST the file to /member/import and show the result summary */
  submitImport(): void {
    if (!this.selectedFile) return;

    this.importing    = true;
    this.importResult = null;

    const formData = new FormData();
    formData.append('file', this.selectedFile);

    this.http.post<{ imported: number; skipped: number; errors: string[] }>(
      CONFIG.URL_BASE + '/member/import',
      formData
    ).subscribe({
      next: (result) => {
        this.importing    = false;
        this.importResult = result;

        if (result.errors?.length === 0) {
          this.toastr.success(`${result.imported} member(s) imported successfully.`, 'Import Complete');
        } else {
          this.toastr.warning(
            `${result.imported} imported, ${result.skipped} skipped. Check errors below.`,
            'Import Partial'
          );
        }

        // Refresh the member list to reflect newly imported records
        this.getAll();
        this.loadNewArrivals();
      },
      error: (err: HttpErrorResponse) => {
        this.importing = false;
        const msg = err.error?.error || err.message || 'Import failed';
        this.toastr.error(msg, 'Import Error');
      }
    });
  }

  /** Reset import state when the modal is dismissed */
  resetImport(): void {
    this.selectedFile     = null;
    this.selectedFileName = '';
    this.importResult     = null;
    this.importing        = false;

    // Reset the file input value so the same file can be re-selected
    const input = document.getElementById('importFile') as HTMLInputElement;
    if (input) input.value = '';
  }

  /** Trigger a download of the pre-built import template */
  downloadTemplate(event: Event): void {
    event.preventDefault();
    const link = document.createElement('a');
    // Serve the template from your Spring Boot static resources folder:
    // src/main/resources/static/templates/members_import_template.xlsx
    link.href = CONFIG.URL_BASE + '/templates/members_import_template.xlsx';
    link.download = 'members_import_template.xlsx';
    link.click();
  }
}